import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users, ChevronLeft, RefreshCw, Loader2, AlertCircle, Check, Trash2, Search,
} from "lucide-react";
import {
  useAdminCommitteeQueueQuery,
  useAdminAssignCommitteeBulkMutation,
  useAdminUnassignCommitteeMutation,
} from "../api/upgradeApi";

/**
 * Assign upgrade applications to Membership Committee members.
 *
 * Laid out like the logbook assessment screen, because it is the same job: a
 * queue of work, a pool of people, and a batch handed from one to the other.
 * Assigning thirty applications one dialog at a time is not a workflow anyone
 * finishes.
 */

const ROLE_LABEL: Record<string, string> = {
  chairman: "Chairman",
  secretary: "Secretary",
  member: "Member",
};

const DECISION_STYLE: Record<string, string> = {
  recommended: "bg-emerald-50 text-emerald-700 border-emerald-200",
  not_recommended: "bg-rose-50 text-rose-700 border-rose-200",
  pending: "bg-amber-50 text-amber-700 border-amber-200",
};

export default function AdminCommitteeAssignmentPage() {
  const { data, isLoading, isFetching, refetch } = useAdminCommitteeQueueQuery();
  const [assignBulk, { isLoading: assigning }] = useAdminAssignCommitteeBulkMutation();
  const [unassign, { isLoading: unassigning }] = useAdminUnassignCommitteeMutation();

  const [assessor, setAssessor] = useState<string>("");
  const [picked, setPicked] = useState<string[]>([]);
  const [q, setQ] = useState("");
  const [banner, setBanner] = useState<{ ok: boolean; text: string } | null>(null);

  const committee = data?.committee;
  const members: any[] = useMemo(() => data?.members ?? [], [data]);
  const awaiting: any[] = useMemo(() => data?.awaiting ?? [], [data]);
  const assigned: any[] = useMemo(() => data?.assigned ?? [], [data]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return awaiting;
    return awaiting.filter((a) =>
      `${a.name ?? ""} ${a.membership_no ?? ""} ${a.to_grade ?? ""}`.toLowerCase().includes(s),
    );
  }, [awaiting, q]);

  const toggle = (id: string) =>
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const allPicked = filtered.length > 0 && filtered.every((a) => picked.includes(a.id));
  const toggleAll = () =>
    setPicked(allPicked ? [] : filtered.map((a) => a.id));

  const doAssign = async () => {
    setBanner(null);
    try {
      const res: any = await assignBulk({ member_id: assessor, application_ids: picked }).unwrap();
      setPicked([]);
      setBanner({ ok: true, text: res?.message ?? "Assigned." });
    } catch (e: any) {
      setBanner({ ok: false, text: e?.data?.message || "Could not assign. Please try again." });
    }
  };

  const doUnassign = async (appId: string, reviewId: number) => {
    setBanner(null);
    try {
      await unassign({ id: appId, reviewId }).unwrap();
      setBanner({ ok: true, text: "Assignment removed." });
    } catch (e: any) {
      setBanner({ ok: false, text: e?.data?.message || "Could not remove that assignment." });
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto p-6 space-y-4">
        <div className="h-24 bg-gray-100 rounded-2xl animate-pulse" />
        <div className="h-64 bg-gray-100 rounded-2xl animate-pulse" />
      </div>
    );
  }

  // Without a committee there is nobody to assign to — say where to fix it.
  if (!committee) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <Link to="/admin/upgrade-applications" className="text-sm text-gray-500 hover:underline">
          ← Grade Upgrades
        </Link>
        <div className="mt-4 bg-white border border-rose-100 rounded-2xl p-8 text-center">
          <AlertCircle className="w-8 h-8 text-rose-400 mx-auto" />
          <h2 className="mt-3 font-black text-gray-900">No active Membership Committee</h2>
          <p className="mt-1 text-sm text-gray-500">
            {data?.message ??
              "Set up the Membership Committee on the active executive set before assigning reviewers."}
          </p>
          <Link
            to="/executive-sets"
            className="inline-block mt-4 px-4 py-2 rounded-xl bg-gray-900 text-white text-xs font-bold"
          >
            Go to Executive Sets
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-8 space-y-6">
      {/* Header */}
      <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
        <Link
          to="/admin/upgrade-applications"
          className="inline-flex items-center gap-1 text-xs font-bold text-gray-500 hover:text-gray-900"
        >
          <ChevronLeft className="w-3.5 h-3.5" /> Grade Upgrades
        </Link>

        <div className="mt-3 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-gray-900 tracking-tight">{committee.name}</h1>
              <p className="text-sm font-semibold text-gray-500">
                {awaiting.length} awaiting assignment · {assigned.length} already with a reviewer
              </p>
            </div>
          </div>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="p-2.5 bg-white border border-gray-100 rounded-xl hover:bg-gray-50 shadow-sm"
          >
            <RefreshCw className={`w-4 h-4 text-gray-500 ${isFetching ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {banner && (
        <div
          className={`px-4 py-3 rounded-2xl text-sm font-semibold border ${
            banner.ok
              ? "bg-emerald-50 border-emerald-100 text-emerald-800"
              : "bg-rose-50 border-rose-100 text-rose-800"
          }`}
        >
          {banner.text}
        </div>
      )}

      {/* ── Committee members ─────────────────────────────────────────── */}
      <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
        <h2 className="text-lg font-black text-gray-900">Committee members</h2>
        <p className="text-sm text-gray-500 mb-4">
          Pending counts are what each member currently has waiting on them.
        </p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {members.map((m) => (
            <div key={m.member_id} className="rounded-2xl border border-gray-100 p-4">
              <div className="font-bold text-gray-900 truncate">{m.name}</div>
              <div className="text-[11px] font-semibold text-gray-400 mt-0.5">
                {[ROLE_LABEL[m.committee_role] ?? m.committee_role, m.membership_no]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
              <div
                className={`mt-3 inline-flex items-center px-2 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${
                  m.pending_count > 0
                    ? "bg-amber-50 text-amber-700 border-amber-200"
                    : "bg-gray-50 text-gray-400 border-gray-200"
                }`}
              >
                {m.pending_count} pending
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Assign awaiting applications ──────────────────────────────── */}
      <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-black text-gray-900">Assign awaiting applications</h2>
            <p className="text-sm text-gray-500">
              Applications at committee review with nobody assigned yet.
            </p>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name or membership no…"
              className="pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-gray-100"
            />
          </div>
        </div>

        {awaiting.length === 0 ? (
          <p className="mt-6 text-sm text-gray-400">
            Nothing is waiting — every application at committee review has a reviewer.
          </p>
        ) : (
          <>
            <div className="mt-4 border rounded-2xl overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] font-black uppercase tracking-widest text-gray-400 border-b">
                    <th className="px-4 py-2 w-10">
                      <input type="checkbox" className="h-4 w-4" checked={allPicked} onChange={toggleAll} />
                    </th>
                    <th className="px-4 py-2">Applicant</th>
                    <th className="px-4 py-2">Upgrade</th>
                    <th className="px-4 py-2">Route</th>
                    <th className="px-4 py-2 text-right">Waiting</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filtered.map((a) => (
                    <tr key={a.id} className="hover:bg-gray-50/60">
                      <td className="px-4 py-2.5">
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          checked={picked.includes(a.id)}
                          onChange={() => toggle(a.id)}
                        />
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="font-semibold text-gray-900">{a.name || "—"}</div>
                        <div className="text-[11px] text-gray-400">{a.membership_no || a.email}</div>
                      </td>
                      <td className="px-4 py-2.5 text-gray-600">
                        {[a.from_grade, a.to_grade].filter(Boolean).join(" → ") || "—"}
                      </td>
                      <td className="px-4 py-2.5 text-gray-500">{a.route || "—"}</td>
                      <td
                        className={`px-4 py-2.5 text-right tabular-nums font-bold ${
                          (a.waiting_days ?? 0) > 30 ? "text-rose-600" : "text-gray-500"
                        }`}
                      >
                        {a.waiting_days != null ? `${a.waiting_days}d` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
              <select
                value={assessor}
                onChange={(e) => setAssessor(e.target.value)}
                className="px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white outline-none focus:ring-2 focus:ring-gray-100"
              >
                <option value="">Assign to…</option>
                {members.map((m) => (
                  <option key={m.member_id} value={m.member_id}>
                    {m.name} ({m.pending_count} pending)
                  </option>
                ))}
              </select>
              <button
                onClick={doAssign}
                disabled={assigning || !assessor || picked.length === 0}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 disabled:opacity-50"
              >
                {assigning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                Assign {picked.length || ""}
              </button>
            </div>
          </>
        )}
      </div>

      {/* ── Already assigned ──────────────────────────────────────────── */}
      <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
        <h2 className="text-lg font-black text-gray-900">Assigned applications</h2>
        <p className="text-sm text-gray-500 mb-4">
          Who is reviewing what, and what they have decided so far.
        </p>

        {assigned.length === 0 ? (
          <p className="text-sm text-gray-400">Nothing has been assigned yet.</p>
        ) : (
          <div className="space-y-3">
            {assigned.map((a) => (
              <div key={a.id} className="rounded-2xl border border-gray-100 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <Link
                      to={`/admin/upgrade-applications/${a.id}`}
                      className="font-bold text-gray-900 hover:text-indigo-600"
                    >
                      {a.name || "—"}
                    </Link>
                    <div className="text-[11px] text-gray-400">
                      {[a.membership_no, [a.from_grade, a.to_grade].filter(Boolean).join(" → ")]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                  </div>
                  <span className="text-[11px] font-semibold text-gray-400">
                    {a.waiting_days != null ? `${a.waiting_days}d at review` : ""}
                  </span>
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  {a.reviews.map((r: any) => {
                    const decision = String(r.decision ?? "pending");
                    return (
                      <span
                        key={r.id}
                        className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${
                          DECISION_STYLE[decision] ?? DECISION_STYLE.pending
                        }`}
                      >
                        {r.name}
                        <span className="opacity-60 uppercase text-[9px] font-black tracking-widest">
                          {decision.replace(/_/g, " ")}
                        </span>
                        {/* Only an untouched assignment can be withdrawn — a
                            recorded decision is part of the record. */}
                        {decision === "pending" && (
                          <button
                            onClick={() => doUnassign(a.id, r.id)}
                            disabled={unassigning}
                            title="Remove this assignment"
                            className="ml-0.5 rounded hover:text-rose-700 disabled:opacity-50"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </span>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
